import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '~env';
import { BillSearchResult, PaymentBillSource } from './payment.model';

interface BillSearchSource {
  source: PaymentBillSource;
  url: string;
  headers?: HttpHeaders;
  /** Any error of an optional source is treated as "not found" so it cannot break the other searches */
  optional?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentQueryService {
  private readonly registrarOdataApi = environment.projectionQueryApi;
  private readonly legalEntityV3QueryApi = environment.legalEntityV3QueryApi;
  private readonly disciplinaryBoardApi = environment.disciplinaryBoardApi;
  private readonly farpQueryApi = environment.farpQueryApi;
  private readonly anonymousPaymentHeaders = new HttpHeaders({
    'X-Api-Key': environment.anonymousPaymentApiKey,
  });

  constructor(private http: HttpClient) {}

  searchBill(billNumber: string, payerName: string, amount: number): Observable<BillSearchResult | null> {
    const params = new HttpParams()
      .set('billNumber', billNumber)
      .set('payerName', payerName)
      .set('amount', amount?.toString());

    const sources: BillSearchSource[] = [
      { source: PaymentBillSource.Student, url: `${this.registrarOdataApi}/SearchBill` },
      { source: PaymentBillSource.Member, url: `${this.registrarOdataApi}/SearchMemberBill` },
      {
        source: PaymentBillSource.DisciplinaryBoard,
        url: `${this.disciplinaryBoardApi}/Financial/SearchBill`,
      },
      {
        source: PaymentBillSource.LegalEntity,
        url: `${this.legalEntityV3QueryApi}/AccountingBatch/SearchBill`,
      },
    ];

    if (this.farpQueryApi) {
      sources.push({
        source: PaymentBillSource.FarpOdq,
        url: `${this.farpQueryApi}/Franchise/SearchBill`,
        headers: this.anonymousPaymentHeaders,
        optional: true,
      });
    }

    return this.searchAcrossSources(sources, params, 0);
  }

  private searchAcrossSources(
    sources: BillSearchSource[],
    params: HttpParams,
    index: number
  ): Observable<BillSearchResult | null> {
    if (index >= sources.length) {
      return of(null);
    }

    const { source, url, headers, optional } = sources[index];
    return this.http.get<any>(url, { params, headers }).pipe(
      map((response) => this.parseBillSearchResult(this.unwrapApiResponse(response), source)),
      catchError((error) => {
        if (error?.status === 404 || optional) {
          if (error?.status !== 404) {
            console.warn(`Bill search failed for source ${source}`, error);
          }
          return this.searchAcrossSources(sources, params, index + 1);
        }
        return throwError(() => error);
      })
    );
  }

  private unwrapApiResponse<T>(response: T): T {
    const wrapped = response as { content?: T; Content?: T };
    return (wrapped?.content ?? wrapped?.Content ?? response) as T;
  }

  private parseBillSearchResult(item: any, source: PaymentBillSource): BillSearchResult {
    const billLines = (item.billLines || []).map((line: any) => this.parseBillLine(line));
    const totalTpsAmount = (item.billLines || []).reduce(
      (total: number, line: any) => total + (line.tpsAmount || 0),
      0
    );
    const totalTvqAmount = (item.billLines || []).reduce(
      (total: number, line: any) => total + (line.tvqAmount || 0),
      0
    );

    const payerFirstName =
      item.payerFirstName || item.studentFirstName || item.memberFirstName || item.customerFirstName || '';
    const payerLastName =
      item.payerLastName || item.studentLastName || item.memberLastName || item.customerLastName || '';

    return {
      billId: item.billId,
      billCode: item.billCode || item.studentBillCode || item.memberBillCode || item.number || '',
      billDate: item.billDate ? new Date(item.billDate) : new Date(),
      amount: item.amount,
      payerFirstName,
      payerLastName,
      payerFullName:
        item.payerFullName ||
        item.studentFullName ||
        `${payerFirstName} ${payerLastName}`.trim(),
      payerId: item.payerId || item.studentId || item.memberId,
      source: item.source || source,
      billLines,
      tpsAmount: item.tpsAmount ?? totalTpsAmount,
      tvqAmount: item.tvqAmount ?? totalTvqAmount,
    };
  }

  private parseBillLine(item: any) {
    return {
      billLineId: item.billLineId || item.id,
      description: { fr: item.descriptionFr, en: item.descriptionEn },
      amount: item.amount,
      quantity: item.quantity || 1,
    };
  }
}
